import React, { useState, useEffect } from 'react';
import { labelsApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Printer, Loader2, Minus, Plus, Trash2, Eye } from 'lucide-react';

const LabelPrintDialog = ({ open, onOpenChange, items, products }) => {
  const [labelItems, setLabelItems] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewProductId, setPreviewProductId] = useState(null);

  useEffect(() => {
    if (open && items?.length) {
      const mapped = items.map(item => {
        const prod = products?.find(p => p.id === item.product_id);
        return {
          product_id: item.product_id,
          reference: prod?.reference || item.reference || '',
          designation: prod?.designation || item.designation || '',
          risk_class: prod?.risk_class || item.risk_class || '',
          quantity: item.quantity || 1,
        };
      });
      setLabelItems(mapped);
      // Auto-preview the first product
      if (mapped.length > 0) {
        loadPreview(mapped[0].product_id);
      }
    }
    if (!open) {
      setPreviewUrl(null);
      setPreviewProductId(null);
    }
  }, [open, items, products]);

  const loadPreview = async (productId) => {
    if (previewProductId === productId && previewUrl) return;
    setPreviewLoading(true);
    setPreviewProductId(productId);
    try {
      const res = await labelsApi.preview(productId);
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch {
      setPreviewUrl(null);
    }
    setPreviewLoading(false);
  };

  const updateQty = (idx, val) => {
    const next = [...labelItems];
    next[idx] = { ...next[idx], quantity: Math.max(0, parseInt(val) || 0) };
    setLabelItems(next);
  };

  const removeItem = (idx) => {
    const removed = labelItems[idx];
    const next = labelItems.filter((_, i) => i !== idx);
    setLabelItems(next);
    if (removed.product_id === previewProductId && next.length > 0) {
      loadPreview(next[0].product_id);
    } else if (next.length === 0) {
      setPreviewUrl(null);
      setPreviewProductId(null);
    }
  };

  const totalLabels = labelItems.reduce((sum, i) => sum + i.quantity, 0);

  const handleGenerate = async () => {
    const toGenerate = labelItems.filter(i => i.quantity > 0);
    if (!toGenerate.length) {
      toast.error('Aucune étiquette à générer');
      return;
    }
    setGenerating(true);
    try {
      const res = await labelsApi.generate(toGenerate.map(i => ({ product_id: i.product_id, quantity: i.quantity })));
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiquettes_${toGenerate.length}_produits.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${totalLabels} étiquettes générées`);
      onOpenChange(false);
    } catch {
      toast.error('Erreur lors de la génération');
    }
    setGenerating(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2">
            <Printer className="w-5 h-5 text-amber-400" />
            Impression d'étiquettes
            <Badge variant="outline" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30 ml-2">
              101.6 x 76.2 mm
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-5 gap-4">
          {/* Left: Product list */}
          <div className="col-span-3 space-y-2">
            <p className="text-zinc-500 text-xs">Contrôlez le nombre d'étiquettes par produit. Cliquez sur l'oeil pour prévisualiser.</p>

            <div className="space-y-1 max-h-[45vh] overflow-y-auto" data-testid="label-items-list">
              <div className="grid grid-cols-12 gap-2 px-2 py-1 text-[10px] text-zinc-500 font-semibold uppercase sticky top-0 bg-zinc-900 z-10">
                <span className="col-span-2">Réf.</span>
                <span className="col-span-4">Désignation</span>
                <span className="col-span-1">ADR</span>
                <span className="col-span-3 text-center">Quantité</span>
                <span className="col-span-2"></span>
              </div>

              {labelItems.map((item, idx) => (
                <div key={idx}
                  className={`grid grid-cols-12 gap-2 items-center px-2 py-1.5 rounded text-sm cursor-pointer transition-colors ${previewProductId === item.product_id ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-zinc-950/50 border border-transparent hover:border-zinc-700'}`}
                  onClick={() => loadPreview(item.product_id)}>
                  <span className="col-span-2 font-mono text-cyan-400 text-xs truncate">{item.reference}</span>
                  <span className="col-span-4 text-zinc-300 text-xs truncate">{item.designation}</span>
                  <span className="col-span-1">
                    {item.risk_class && (
                      <Badge variant="outline" className="text-[9px] px-1">{item.risk_class}</Badge>
                    )}
                  </span>
                  <div className="col-span-3 flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-zinc-400"
                      onClick={() => updateQty(idx, item.quantity - 1)}>
                      <Minus className="w-3 h-3" />
                    </Button>
                    <Input type="number" value={item.quantity}
                      onChange={(e) => updateQty(idx, e.target.value)}
                      className="w-14 h-6 bg-zinc-900 border-zinc-700 text-white text-center font-mono text-xs"
                      min="0" data-testid={`label-qty-${idx}`} />
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-zinc-400"
                      onClick={() => updateQty(idx, item.quantity + 1)}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="col-span-2 flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className={`h-6 w-6 p-0 ${previewProductId === item.product_id ? 'text-amber-400' : 'text-zinc-500'}`}
                      onClick={() => loadPreview(item.product_id)} data-testid={`preview-label-${idx}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400"
                      onClick={() => removeItem(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Preview */}
          <div className="col-span-2 flex flex-col items-center">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-2">Aperçu étiquette</p>
            <div className="w-full aspect-[4/3] bg-zinc-950 rounded-lg border border-zinc-800 flex items-center justify-center overflow-hidden relative">
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/80 z-10">
                  <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                </div>
              )}
              {previewUrl ? (
                <img src={previewUrl} alt="Aperçu étiquette" className="max-w-full max-h-full object-contain p-2" data-testid="label-preview-image" />
              ) : (
                <div className="text-center p-4">
                  <Eye className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-600 text-xs">Sélectionnez un produit</p>
                </div>
              )}
            </div>
            <p className="text-[9px] text-zinc-600 mt-1 text-center">
              Rendu réel : 101.6 x 76.2 mm
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
          <div className="text-sm">
            <span className="text-zinc-400">{labelItems.filter(i => i.quantity > 0).length} produits</span>
            <span className="text-white font-bold ml-3">{totalLabels} étiquettes</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-zinc-700" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={handleGenerate} className="bg-amber-500 hover:bg-amber-400 text-black"
              disabled={generating || totalLabels === 0} data-testid="generate-labels-button">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
              Imprimer {totalLabels} étiquettes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LabelPrintDialog;
